import React, { useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { View, Text, Pressable } from '../../lib/react-native-compat';
import { jobPhaseCodesAtom } from '../../store/company';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import { isBlankRow } from './sectionDefs';
import { moduleForCode } from './qtyModules';
import { useJobPhases } from './WorkPhaseChips';
import useQtyHistory from './useQtyHistory';
import { usePhaseMetrics, phaseStatus, evenPeriodSpread } from './usePhaseMetrics';
import AutoGrowTextarea from './AutoGrowTextarea';

// Quick Report Progress tab: one card per phase picked on the Gantt/chips.
// Each card is the phase's pace at a glance — plan window vs projected window,
// then a four-day quantity grid (three days back plus today). Past days show
// what was installed against what was planned for that day; today's cells are
// the entry fields. Phases that break out by pipe size get a sub-row per size.
// The day's note closes the card.
//
// Quantity is the only thing tracked here. Labor — naming people and their
// hours — belongs to the full report's Labor step; rows a draft already has in
// the hidden 'timeCard' JSON section still count toward the phase's hours (and
// so toward PF), they just aren't edited on this tab.
//
// Storage: notes are the standard 'work' section rows (costCode +
// description). The qty inputs write the standard 'quantities' typed rows
// (installedToday / unit / budgetQty), one per cost code plus one per pipe size
// (size in the Task column), so review, sheet and SQL reporting see normal
// data.

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const hasCode = (r, code) => Object.prototype.hasOwnProperty.call(r?.hours || {}, code);

const META_CODE = '__meta';

const inputBase = {
    minHeight: 34,
    fontSize: 14,
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '6px 8px',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
};

// How a day's actual reads against what was planned for it. The band is
// deliberately wide at the top — hitting 98% of plan is hitting plan.
//
// The colour rides a fill bar along the bottom of the cell rather than washing
// the whole tile: the number stays high-contrast and legible, and a row of four
// cells reads as one quiet strip instead of four blocks of poster paint.
const TONES = {
    good: { surface: 'linear-gradient(180deg,#FFFFFF 0%,#F2FDF6 100%)', ring: '#C6F0D6', bar: 'linear-gradient(90deg,#34D399 0%,#059669 100%)', fg: '#065F46', sub: '#059669' },
    warn: { surface: 'linear-gradient(180deg,#FFFFFF 0%,#FFFCF0 100%)', ring: '#F7E3A8', bar: 'linear-gradient(90deg,#FBBF24 0%,#D97706 100%)', fg: '#92400E', sub: '#B45309' },
    bad: { surface: 'linear-gradient(180deg,#FFFFFF 0%,#FFF5F5 100%)', ring: '#F8CFCF', bar: 'linear-gradient(90deg,#F87171 0%,#DC2626 100%)', fg: '#991B1B', sub: '#DC2626' },
    none: { surface: 'linear-gradient(180deg,#FFFFFF 0%,#F8FAFC 100%)', ring: '#E7EDF4', bar: 'linear-gradient(90deg,#CBD5E1 0%,#94A3B8 100%)', fg: '#0F172A', sub: '#94A3B8' },
};

// Shared cell shell — soft radius, hairline ring, one-pixel lift. Both the
// read-only days and today's entry sit in the same box so the row lines up.
const cellShell = (tone) => ({
    flex: 1,
    minWidth: 0,
    minHeight: 54,
    borderRadius: 10,
    backgroundImage: tone.surface,
    border: `1px solid ${tone.ring}`,
    boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 7,
    paddingRight: 7,
    boxSizing: 'border-box',
    alignItems: 'center',
    justifyContent: 'center',
});

// The fill bar: how far the day got toward its plan.
const PlanBar = ({ actual, plan, tone }) => (
    <View style={{ width: '100%', height: 3, borderRadius: 999, backgroundColor: '#EDF1F6', marginTop: 5, overflow: 'hidden' }}>
        <View style={{
            height: '100%',
            width: `${Math.max(0, Math.min((actual || 0) / plan, 1)) * 100}%`,
            borderRadius: 999,
            backgroundImage: tone.bar,
        }} />
    </View>
);

// A plan under half a unit a day rounds to "0" on screen, which reads as "you
// were told to do nothing" rather than "this phase is a rounding error spread
// over months". Below the threshold the cell just shows the actual.
const showsPlan = (plan) => plan != null && plan >= 0.5;

const toneFor = (actual, plan) => {
    if (plan == null || !(plan > 0) || actual == null) return TONES.none;
    const ratio = actual / plan;
    if (ratio >= 0.98) return TONES.good;
    if (ratio >= 0.75) return TONES.warn;
    return TONES.bad;
};

// Whole numbers once they're big enough that a decimal is noise.
const fmtV = (v) => {
    if (v == null || !Number.isFinite(v)) return '—';
    const r = v >= 100 ? Math.round(v) : Math.round(v * 10) / 10;
    return r.toLocaleString();
};
const fmtQ = (n) => Math.round(n).toLocaleString();

// One figure in the header strip: label over value, equal share of the width,
// hairline divider between. Stacking the label reads better than the old
// label-beside-value form once there are five of them across the card.
const HeadStat = ({ label, value, tone, title }) => (
    <View
        title={title}
        style={{
            flex: 1, minWidth: 0, paddingLeft: 10, paddingRight: 10,
            borderLeft: '1px solid #E2E8F0',
        }}
    >
        <Text numberOfLines={1} style={{ fontSize: 8.5, fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {label}
        </Text>
        <Text
            numberOfLines={1}
            style={{
                fontSize: 12.5, fontWeight: '700', lineHeight: '16px', whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis',
                color: tone === 'bad' ? '#B91C1C' : tone === 'good' ? '#047857' : '#0F172A',
            }}
        >
            {value}
        </Text>
    </View>
);

// Plan and projection stacked, start dates in one column and finish dates in
// another, so the drift reads straight down the card — 4/20 over 6/4 says
// "started six weeks late" without anyone doing the arithmetic. The badge on
// the finish names the slip outright.
const DateLine = ({ label, start, end, tone, badge, title }) => {
    const color = tone === 'bad' ? '#B91C1C' : tone === 'good' ? '#047857' : tone === 'plan' ? '#1D4ED8' : '#0F172A';
    return (
        <View title={title} style={{ flexDirection: 'row', alignItems: 'baseline', columnGap: 5 }}>
            <Text style={{ width: 62, flexShrink: 0, fontSize: 8.5, fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                {label}
            </Text>
            <Text style={{ width: 34, flexShrink: 0, textAlign: 'right', fontSize: 12.5, fontWeight: '700', lineHeight: '15px', color, fontVariantNumeric: 'tabular-nums' }}>
                {start}
            </Text>
            <Text style={{ flexShrink: 0, fontSize: 10, color: '#CBD5E1' }}>–</Text>
            <Text style={{ width: 40, flexShrink: 0, fontSize: 12.5, fontWeight: '700', lineHeight: '15px', color, fontVariantNumeric: 'tabular-nums' }}>
                {end}
            </Text>
            {badge}
        </View>
    );
};

// How far the projected finish lands past (or inside) the planned one.
const SlipBadge = ({ days }) => {
    const late = days > 0;
    return (
        <View style={{
            flexShrink: 0, borderRadius: 999, paddingTop: 0, paddingBottom: 0, paddingLeft: 5, paddingRight: 5,
            backgroundColor: late ? '#FEE2E2' : days < 0 ? '#D1FAE5' : '#E2E8F0',
        }}>
            <Text style={{
                fontSize: 9, fontWeight: '700', lineHeight: '14px', whiteSpace: 'nowrap',
                color: late ? '#B91C1C' : days < 0 ? '#047857' : '#475569',
            }}>
                {days === 0 ? 'on time' : `${late ? '+' : '−'}${Math.abs(days)}d`}
            </Text>
        </View>
    );
};

// One past day: what happened, with the plan for that day as the small figure
// beside it. Colour comes from the two together, so a blank cell on a day
// nothing was planned stays neutral instead of reading as a miss.
const DayCell = ({ actual, plan, title }) => {
    const tone = toneFor(actual, plan);
    const withPlan = showsPlan(plan);
    return (
        <View title={title} style={cellShell(tone)}>
            <Text style={{ fontSize: 17, fontWeight: '700', lineHeight: '20px', letterSpacing: -0.3, color: actual ? tone.fg : '#C7D2DE', fontVariantNumeric: 'tabular-nums' }}>
                {actual == null ? '–' : fmtV(actual)}
            </Text>
            {withPlan ? (
                <Text style={{ fontSize: 9, fontWeight: '600', lineHeight: '12px', color: tone.sub, fontVariantNumeric: 'tabular-nums' }}>
                    {`of ${fmtV(plan)}`}
                </Text>
            ) : null}
            {withPlan ? <PlanBar actual={actual} plan={plan} tone={tone} /> : null}
        </View>
    );
};

// Today's cell — same tone treatment wrapped around the entry field, so the
// box turns green the moment the number typed meets the day's plan.
const TodayCell = ({ value, plan, onChange, readOnly, placeholder, title }) => {
    const typed = value === '' || value == null ? null : num(value);
    const tone = toneFor(typed, plan);
    const withPlan = showsPlan(plan);
    return (
        <View title={title} style={cellShell(tone)}>
            <input
                type="number"
                inputMode="decimal"
                value={value ?? ''}
                disabled={readOnly}
                placeholder={placeholder || '0'}
                onChange={(e) => onChange(e.target.value)}
                style={{
                    width: '100%', minWidth: 0, boxSizing: 'border-box', minHeight: 28,
                    border: `1px solid ${tone.ring}`, borderRadius: 8, padding: '2px 4px',
                    fontSize: 17, fontWeight: 700, letterSpacing: -0.3, textAlign: 'center',
                    color: tone.fg, backgroundColor: '#fff', fontFamily: 'inherit', outline: 'none',
                    boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.06)',
                }}
            />
            {withPlan ? (
                <Text style={{ fontSize: 9, fontWeight: '600', lineHeight: '12px', color: tone.sub, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                    {`of ${fmtV(plan)}`}
                </Text>
            ) : null}
            {withPlan ? <PlanBar actual={typed} plan={plan} tone={tone} /> : null}
        </View>
    );
};

const RowLabel = ({ children, sub }) => (
    <View style={{ width: 64, flexShrink: 0 }}>
        <Text style={{ fontSize: 9, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: '11px' }}>
            {children}
        </Text>
        {sub ? (
            <Text style={{ fontSize: 9, color: '#94A3B8', lineHeight: '11px' }}>{sub}</Text>
        ) : null}
    </View>
);

const TimeCard = ({ readOnly }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);
    const [sizeDraft, setSizeDraft] = useState({});
    const qtyHist = useQtyHistory(report);
    const { jobPhases, intervals } = useJobPhases(qtyHist);
    // JDE actual labour hours (they drive PF and the actual-start date),
    // HeavyJob FP quantities, phased Burn Tool budget hours and the
    // Productivity Tracker's installed counts — same sources as the Gantt's
    // B/E/A rows.
    const { actualHours, fpQty, phasedHours, trackerInstalled } = usePhaseMetrics();

    if (!report) return null;

    // Phases picked on the Work tab, in pick order.
    const seen = new Set();
    const codes = [];
    for (const r of report.sections?.work || []) {
        const code = String(r?.costCode || '').trim();
        if (!code || seen.has(code)) continue;
        seen.add(code);
        codes.push(code);
    }

    const descOf = (c) => jobPhases[c] || phaseCodes?.[c] || '';
    const modOf = (c) => moduleForCode(c, descOf(c));
    const uomOf = (c) => modOf(c)?.uom || '';

    const crewRows = report.sections?.timeCard || [];
    const qtyRows = report.sections?.quantities || [];
    const workRows = report.sections?.work || [];
    const detailRows = report.sections?.qtyDetail || [];
    // A phase's flat quantity rows are keyed by cost code + Task (the pipe
    // size); the unsized row carries Task ''.
    const qtyRowOf = (code, task = '') => qtyRows.find(
        (r) => String(r?.costCode || '').trim() === code && String(r?.task || '').trim() === task,
    ) || {};
    const workRowOf = (code) => workRows.find((r) => String(r?.costCode || '').trim() === code) || {};

    // Pipe sizes on the job: this report's meta row wins, else the list the
    // latest prior report saved.
    const metaRow = detailRows.find((r) => r?.costCode === META_CODE);
    const sizes = (Array.isArray(metaRow?.sizes) ? metaRow.sizes : qtyHist.sizes) || [];

    // One functional setReport per edit — the patch is computed from the
    // freshest sections so rapid edits never clobber each other. Any of the
    // sections can change together (hours edits keep MH Today in sync).
    const update = (fn) => {
        setReport((rep) => (rep ? { ...rep, sections: { ...rep.sections, ...fn(rep.sections) } } : rep));
        setDirty(true);
    };

    const colHours = (crew, code) => crew.reduce((t, r) => t + (hasCode(r, code) ? num(r.hours[code]) : 0), 0);

    // Installed-to-date floor for a phase: the field-report history, the
    // aggregate ToDate sum (jobs whose function deploy predates the per-day
    // recordset) and the Productivity Tracker's installed count, whichever is
    // highest. The PM keeps the tracker current between daily reports.
    const histToDate = (code, task) => (task
        ? (qtyHist.sizeToDate?.[code]?.[task] || 0)
        : Math.max(qtyHist.toDate[code] || 0, trackerInstalled[code] || 0));

    // Upsert one typed quantities row (cost code + size). New rows prefill unit
    // from the tracker registry and budget from the schedule / prior reports;
    // budget rides only the code's first row so per-code reads stay sane.
    const patchQty = (list, code, task, patch) => {
        const idx = list.findIndex(
            (r) => String(r?.costCode || '').trim() === code && String(r?.task || '').trim() === task,
        );
        const firstOfCode = !list.some((r) => String(r?.costCode || '').trim() === code);
        const base = idx >= 0 ? list[idx] : {
            costCode: code,
            task,
            unit: uomOf(code),
            budgetQty: firstOfCode && qtyHist.budgets[code] ? String(qtyHist.budgets[code]) : '',
        };
        const next = { ...base, task, ...patch };
        next.installedToDate = String(histToDate(code, task) + num(next.installedToday));
        const out = [...list];
        if (idx >= 0) out[idx] = next; else out.push(next);
        return out;
    };

    const setNote = (code, value) => update((sections) => {
        const rows = sections.work || [];
        const idx = rows.findIndex((r) => String(r?.costCode || '').trim() === code);
        return {
            work: idx >= 0
                ? rows.map((r, i) => (i === idx ? { ...r, description: value } : r))
                : [...rows, { costCode: code, description: value }],
        };
    });

    const setQty = (code, task, field, value) => update((sections) => ({
        quantities: patchQty(sections.quantities || [], code, task, { [field]: value }),
    }));

    // Pipe-size list for the job, stored on the qtyDetail '__meta' row — the
    // same row the Quantities step reads, so both stay in step.
    const setSizes = (list) => update((sections) => {
        const clean = [...new Set(list.map((s) => String(s).trim()).filter(Boolean))];
        const rows = sections.qtyDetail || [];
        const idx = rows.findIndex((r) => r?.costCode === META_CODE);
        return {
            qtyDetail: idx >= 0
                ? rows.map((r, i) => (i === idx ? { ...r, sizes: clean } : r))
                : [...rows, { costCode: META_CODE, sizes: clean }],
        };
    });

    if (codes.length === 0) {
        return (
            <View style={{ borderRadius: 10, border: '1px dashed #CBD5E1', backgroundColor: '#F8FAFC', padding: 16, alignItems: 'center' }}>
                <Text style={{ fontSize: 13, color: '#64748B', textAlign: 'center' }}>
                    Tap the phases worked above — each one gets a card for the day's quantities and note.
                </Text>
            </View>
        );
    }

    // The four columns: three days back, then today.
    const columns = [];
    for (let i = 3; i >= 0; i--) {
        const d = moment(report.reportDate).subtract(i, 'days');
        columns.push({
            key: d.format('YYYY-MM-DD'),
            head: i === 0 ? 'Today' : i === 1 ? 'Yest' : `-${i}`,
            date: d.format('ddd M/D'),
            isToday: i === 0,
        });
    }

    return (
        <View style={{ borderRadius: 12, border: '1px solid #E2E8F0', backgroundColor: '#fff', overflow: 'hidden' }}>
            {codes.map((code, idx) => {
                const desc = descOf(code);
                const mod = modOf(code);
                const uom = uomOf(code);
                const iv = intervals[code];
                const budget = qtyHist.budgets[code] || 0;
                const note = workRowOf(code).description || '';
                const people = crewRows
                    .map((r, i) => ({ r, i }))
                    .filter(({ r }) => hasCode(r, code));
                const phaseHours = colHours(crewRows, code);

                // Sub-rows: one per pipe size when the tracker breaks this code
                // out that way and the job has a size list.
                const sized = !!mod?.pipeSize && sizes.length > 0;
                // Today's roll-up sums every row on the code, not just the
                // sizes on screen: a report typed before the size list existed
                // (or on the Quantities step) carries an unsized row, and
                // dropping it here would silently under-count the phase while
                // the row still saved. When one is there it gets its own
                // sub-row so the number is visible and editable.
                const unsized = num(qtyRowOf(code, '').installedToday);
                const rowTasks = sized ? [...sizes, ...(unsized ? [''] : [])] : [''];
                const todayQty = qtyRows
                    .filter((r) => String(r?.costCode || '').trim() === code)
                    .reduce((t, r) => t + num(r.installedToday), 0);

                // Daily actuals: field-report submissions + HeavyJob FP
                // postings.
                const dailyQty = {};
                (qtyHist.daily?.[code] || []).forEach((d) => { dailyQty[d.date] = (dailyQty[d.date] || 0) + d.qty; });
                Object.entries(fpQty[code] || {}).forEach(([d, v]) => { dailyQty[d] = (dailyQty[d] || 0) + v; });

                const doneQty = Object.entries(dailyQty).reduce((t, [d, v]) => t + (d <= report.reportDate ? v : 0), 0)
                    + todayQty;
                const instToDate = Math.max(
                    doneQty,
                    (qtyHist.toDate[code] || 0) + todayQty,
                    (trackerInstalled[code] || 0) + todayQty,
                );

                // The day's planned quantity: the saved schedule's curve when
                // the job has one, else the budget spread evenly across the
                // Burn Tool periods.
                const planPerDay = qtyHist.planFor(code)?.perDay || evenPeriodSpread(budget, iv)?.perDay || null;
                const plannedQtyOn = (d) => (planPerDay ? planPerDay[d] || 0 : null);

                const statusQty = phaseStatus(budget, instToDate, iv, report.reportDate, qtyHist.planFor(code)?.perDay);
                const budgetHrs = (phasedHours[code] || []).reduce((t, r) =>
                    t + r.daily * (moment(r.end).diff(moment(r.start), 'days') + 1), 0);
                const doneHrs = Object.entries(actualHours[code] || {}).reduce((t, [d, v]) => t + (d <= report.reportDate ? v : 0), 0)
                    + phaseHours;
                const statusHrs = phaseStatus(budgetHrs, doneHrs, iv, report.reportDate);
                const status = statusQty || statusHrs;

                // The strip down the card's left edge is the glance read:
                // green when done keeps up with plan, amber when it has slipped
                // but is still inside the ±5-point on-track band, red when the
                // phase is genuinely behind.
                const RED = { strip: 'linear-gradient(180deg,#F87171 0%,#B91C1C 100%)', pill: 'linear-gradient(135deg,#EF4444 0%,#B91C1C 100%)', tint: 'linear-gradient(135deg,#FFFFFF 0%,#FEF2F2 100%)', fg: '#B91C1C' };
                const GREEN = { strip: 'linear-gradient(180deg,#34D399 0%,#047857 100%)', pill: 'linear-gradient(135deg,#10B981 0%,#047857 100%)', tint: 'linear-gradient(135deg,#FFFFFF 0%,#F0FDF4 100%)', fg: '#047857' };
                const AMBER = { strip: 'linear-gradient(180deg,#FBBF24 0%,#B45309 100%)', pill: 'linear-gradient(135deg,#F59E0B 0%,#B45309 100%)', tint: 'linear-gradient(135deg,#FFFFFF 0%,#FFFBEB 100%)', fg: '#B45309' };
                const statusStyle = status && (
                    status.state === 'behind' ? { ...RED, label: 'Behind' }
                        : status.state === 'ahead' ? { ...GREEN, label: 'Ahead' }
                            : status.pctDone >= status.pctPlanned ? { ...GREEN, label: 'On track' }
                                : { ...AMBER, label: 'On track' }
                );
                const statusDetail = status && `${[
                    statusQty && `${statusQty.pctDone}% qty`,
                    statusHrs && `${statusHrs.pctDone}% hrs`,
                ].filter(Boolean).join(' · ')} vs ${status.pctPlanned}% sched`;

                // Productivity Combined-style read: remaining vs the tracker
                // budget, PF (earned ÷ actual hours — <1 means burning more
                // hours per unit than budgeted) and where the recent pace lands
                // the finish.
                const remaining = budget > 0 ? budget - instToDate : null;
                const pf = budget > 0 && budgetHrs > 0 && doneHrs > 0 && instToDate > 0
                    ? ((instToDate / budget) * budgetHrs) / doneHrs : null;
                const recentPace = ((qtyHist.last7[code] || 0) + todayQty) / 7;

                // When work actually started. Installed quantities say it best,
                // but most jobs post none — so fall back to the first day
                // anyone charged labor hours to the phase, which is what makes
                // the actual window show up at all.
                const firstInstall = Object.keys(dailyQty).filter((d) => dailyQty[d] > 0).sort()[0];
                const firstLabor = Object.keys(actualHours[code] || {}).sort()[0];
                const actualStart = [firstInstall, firstLabor].filter(Boolean).sort()[0] || null;
                const overallPace = actualStart && instToDate > 0
                    ? instToDate / Math.max(moment(report.reportDate).diff(moment(actualStart), 'days') + 1, 1)
                    : 0;
                const pace = recentPace > 0 ? recentPace : overallPace;
                const projFinish = remaining != null && remaining > 0 && pace > 0
                    ? moment(report.reportDate).add(Math.ceil(remaining / pace), 'days')
                    : null;
                const finLate = !!(projFinish && iv?.end && projFinish.isAfter(moment(iv.end), 'day'));
                // Days the projected finish lands past the planned one — the
                // one number that says "this phase is slipping" outright.
                const slipDays = projFinish && iv?.end ? projFinish.diff(moment(iv.end), 'days') : null;

                return (
                    <View
                        key={code}
                        style={{
                            borderTop: idx > 0 ? '1px solid #E2E8F0' : 'none',
                            // Pace strip down the card's left edge — the glance
                            // read, with the card tinted to match.
                            borderLeft: `5px solid ${statusStyle ? 'transparent' : '#E2E8F0'}`,
                            backgroundImage: statusStyle
                                ? `${statusStyle.strip}, ${statusStyle.tint}`
                                : 'linear-gradient(135deg,#FFFFFF 0%,#F8FAFC 100%)',
                            backgroundSize: statusStyle ? '5px 100%, 100% 100%' : '100% 100%',
                            backgroundRepeat: 'no-repeat',
                            backgroundOrigin: 'border-box, padding-box',
                            paddingLeft: statusStyle ? 0 : undefined,
                        }}
                    >
                        {/* Phase header — title and status pill, the done-vs-plan
                            bar, then one evenly-spaced strip of the numbers.
                            The strip replaces the old ragged mix of right-hand
                            dates and cramped micro-stats: every figure gets the
                            same label treatment and the width is used. */}
                        <View style={{ paddingTop: 8, paddingBottom: 7, paddingLeft: 12, paddingRight: 12, borderBottom: '1px solid #E2E8F0' }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 8 }}>
                                <Text style={{ flexShrink: 0, fontSize: 11, fontWeight: '700', color: '#475569', backgroundColor: '#F1F5F9', borderRadius: 6, paddingTop: 1, paddingBottom: 1, paddingLeft: 6, paddingRight: 6, fontVariantNumeric: 'tabular-nums' }}>
                                    {code}
                                </Text>
                                <Text numberOfLines={1} style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: '700', color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {desc}
                                </Text>
                                {status && (
                                    <View
                                        title={statusDetail}
                                        style={{
                                            flexShrink: 0, borderRadius: 999, backgroundImage: statusStyle.pill,
                                            paddingTop: 2, paddingBottom: 2, paddingLeft: 9, paddingRight: 9,
                                        }}
                                    >
                                        <Text style={{ fontSize: 10, fontWeight: '700', color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                                            {statusStyle.label}
                                        </Text>
                                    </View>
                                )}
                            </View>

                            {/* Done against the plan's own pace: the fill is how
                                much of the budget is in, the notch is where the
                                schedule says it should be by today. */}
                            {status && (
                                <View
                                    title={statusDetail}
                                    style={{ position: 'relative', height: 4, borderRadius: 999, backgroundColor: '#E2E8F0', marginTop: 6, overflow: 'hidden' }}
                                >
                                    <View style={{ height: '100%', width: `${Math.max(0, Math.min(status.pctDone, 100))}%`, borderRadius: 999, backgroundImage: statusStyle.pill }} />
                                    <View style={{ position: 'absolute', top: 0, bottom: 0, left: `${Math.max(0, Math.min(status.pctPlanned, 100))}%`, width: 2, backgroundColor: '#334155' }} />
                                </View>
                            )}

                            <View style={{ flexDirection: 'row', alignItems: 'stretch', marginTop: 7 }}>
                                <View style={{ flex: 1.9, minWidth: 0, paddingRight: 10 }}>
                                    <DateLine
                                        label="Plan"
                                        start={iv ? moment(iv.start).format('M/D') : '—'}
                                        end={iv ? moment(iv.end).format('M/D') : '—'}
                                        tone="plan"
                                        title={iv?.source === 'schedule' ? 'From the quantity schedule' : 'From the phased Burn Tool'}
                                    />
                                    <DateLine
                                        label="Projection"
                                        start={actualStart ? moment(actualStart).format('M/D') : '—'}
                                        end={projFinish ? projFinish.format('M/D')
                                            : remaining != null && remaining <= 0 ? 'Done' : '—'}
                                        tone={finLate ? 'bad' : slipDays != null ? 'good' : undefined}
                                        badge={slipDays == null ? null : <SlipBadge days={slipDays} />}
                                        title="First day work actually hit the phase – finish at the current pace"
                                    />
                                </View>
                                <HeadStat
                                    label={uom ? `Installed ${uom}` : 'Installed'}
                                    value={instToDate > 0 ? fmtQ(instToDate) : '—'}
                                    title="Installed to date, including today's entry"
                                />
                                <HeadStat
                                    label="Remaining"
                                    value={remaining == null ? '—' : fmtQ(remaining)}
                                    tone={remaining != null && remaining < 0 ? 'bad' : undefined}
                                    title="Budget quantity less installed to date"
                                />
                                <HeadStat
                                    label="PF"
                                    value={pf == null ? '—' : (Math.round(pf * 100) / 100).toFixed(2)}
                                    tone={pf == null ? undefined : pf < 1 ? 'bad' : 'good'}
                                    title="Earned ÷ actual hours — under 1.00 is burning more hours per unit than budgeted"
                                />
                            </View>
                        </View>

                        <View style={{ paddingTop: 8, paddingBottom: 8, paddingLeft: 12, paddingRight: 12 }}>
                            {/* Four-day progress grid — the card's point. */}
                            <View style={{ flexDirection: 'row', alignItems: 'flex-end', columnGap: 6 }}>
                                <View style={{ width: 64, flexShrink: 0 }} />
                                {columns.map((c) => (
                                    <View key={c.key} style={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
                                        <Text style={{ fontSize: 9, fontWeight: '700', color: c.isToday ? '#1D4ED8' : '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: '11px' }}>
                                            {c.head}
                                        </Text>
                                        <Text style={{ fontSize: 9, color: '#94A3B8', lineHeight: '11px', whiteSpace: 'nowrap' }}>{c.date}</Text>
                                    </View>
                                ))}
                            </View>

                            {/* Quantity — the code total. When the phase splits
                                by pipe size this row is the roll-up and the
                                sizes below are where the numbers get typed. */}
                            <View style={{ flexDirection: 'row', alignItems: 'stretch', columnGap: 6, marginTop: 4 }}>
                                <RowLabel sub={uom || null}>Quantity</RowLabel>
                                {columns.map((c) => (c.isToday && !sized ? (
                                    <TodayCell
                                        key={c.key}
                                        value={qtyRowOf(code, '').installedToday}
                                        plan={plannedQtyOn(c.key)}
                                        readOnly={readOnly}
                                        onChange={(v) => setQty(code, '', 'installedToday', v)}
                                        title={`Installed today${uom ? ` (${uom})` : ''}`}
                                    />
                                ) : (
                                    <DayCell
                                        key={c.key}
                                        actual={c.isToday ? todayQty : (dailyQty[c.key] ?? null)}
                                        plan={plannedQtyOn(c.key)}
                                        title={c.isToday ? 'Total of the sizes below' : `${c.date} — installed${uom ? ` ${uom}` : ''}`}
                                    />
                                )))}
                            </View>

                            {/* One sub-row per pipe size. Per-size plans don't
                                exist, so these carry the numbers and the roll-up
                                above carries the colour. */}
                            {sized && rowTasks.map((task) => (
                                <View key={task || '__unsized'} style={{ flexDirection: 'row', alignItems: 'stretch', columnGap: 6, marginTop: 3 }}>
                                    <View style={{ width: 64, flexShrink: 0, paddingLeft: 8 }}>
                                        <Text numberOfLines={1} style={{ fontSize: 10, fontWeight: '600', color: task ? '#64748B' : '#94A3B8', fontStyle: task ? 'normal' : 'italic', lineHeight: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {task || 'no size'}
                                        </Text>
                                    </View>
                                    {columns.map((c) => (c.isToday ? (
                                        <TodayCell
                                            key={c.key}
                                            value={qtyRowOf(code, task).installedToday}
                                            plan={null}
                                            readOnly={readOnly}
                                            onChange={(v) => setQty(code, task, 'installedToday', v)}
                                            title={task
                                                ? `${task} installed today${uom ? ` (${uom})` : ''}`
                                                : 'Installed today with no pipe size against it'}
                                        />
                                    ) : (
                                        <DayCell
                                            key={c.key}
                                            actual={qtyHist.dailyBySize?.[code]?.[task]?.[c.key] ?? null}
                                            plan={null}
                                            title={`${c.date} — ${task || 'no size'}`}
                                        />
                                    )))}
                                </View>
                            ))}

                            {/* Pipe sizes for codes that track by size but have
                                no list yet — without it the sub-rows can't
                                exist, so the entry point lives right here. */}
                            {!readOnly && !!mod?.pipeSize && (
                                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', columnGap: 6, rowGap: 4, marginTop: 6 }}>
                                    <Text style={{ fontSize: 9, fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Sizes</Text>
                                    {sizes.map((s) => (
                                        <View key={s} style={{ flexDirection: 'row', alignItems: 'center', borderRadius: 999, border: '1px solid #CBD5E1', backgroundColor: '#fff', paddingTop: 1, paddingBottom: 1, paddingLeft: 8, paddingRight: 4 }}>
                                            <Text style={{ fontSize: 10, fontWeight: '600', color: '#475569' }}>{s}</Text>
                                            <Pressable onPress={() => setSizes(sizes.filter((x) => x !== s))} style={{ cursor: 'pointer', paddingLeft: 4, paddingRight: 4 }}>
                                                <Text style={{ fontSize: 11, color: '#DC2626' }}>×</Text>
                                            </Pressable>
                                        </View>
                                    ))}
                                    <input
                                        type="text"
                                        value={sizeDraft[code] ?? ''}
                                        placeholder={'add size (12")'}
                                        onChange={(e) => setSizeDraft({ ...sizeDraft, [code]: e.target.value })}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && (sizeDraft[code] || '').trim()) {
                                                setSizes([...sizes, sizeDraft[code]]);
                                                setSizeDraft({ ...sizeDraft, [code]: '' });
                                            }
                                        }}
                                        style={{ ...inputBase, width: 106, minHeight: 24, padding: '2px 6px', fontSize: 11 }}
                                    />
                                </View>
                            )}

                            {/* Note for the phase (= the work row's description) —
                                last, after the numbers are in */}
                            <AutoGrowTextarea
                                value={note}
                                disabled={readOnly}
                                placeholder="What was done on this phase today?"
                                onChange={(e) => setNote(code, e.target.value)}
                                minHeight={32}
                                style={{ ...inputBase, width: '100%', fontFamily: 'inherit', marginTop: 6, ...(readOnly ? { backgroundColor: '#F1F5F9' } : null) }}
                            />
                        </View>
                    </View>
                );
            })}
        </View>
    );
};

// Total hours on the card — shared with the Quick Report review totals.
export const timeCardTotalHours = (rows) => (rows || [])
    .filter((r) => !isBlankRow(r))
    .reduce((t, r) => t + Object.values(r?.hours || {}).reduce((s, v) => s + num(v), 0), 0);

export default TimeCard;
